import { z } from 'zod';

// ============================================
// Common schemas
// ============================================

export const channelSchema = z.enum(['ios', 'tvos', 'service']);
export const submissionTypeSchema = z.enum(['bug_report', 'feature_request']);
export const submissionStatusSchema = z.enum(['pending', 'approved', 'rejected', 'cancelled']);
export const resolutionSchema = z.enum([
  'in_progress',
  'resolved',
  'wont_fix',
  'duplicate',
  'invalid',
]);
export const prioritySchema = z.enum(['low', 'medium', 'high', 'critical']);
export const adminRoleSchema = z.enum(['owner', 'admin', 'maintainer']);
export const appealStatusSchema = z.enum(['pending', 'approved', 'rejected']);

// ============================================
// Submission schemas
// ============================================

export const createSubmissionSchema = z.object({
  channel: channelSchema,
  type: submissionTypeSchema,
  os_version: z.string().max(20).optional(),
  app_version: z.string().max(20).optional(),
  content: z.string().min(10, 'Описание должно содержать минимум 10 символов').max(2000),
});

export const updateSubmissionSchema = z.object({
  status: z.enum(['approved', 'rejected']).optional(),
  resolution: resolutionSchema.optional(),
  priority: prioritySchema.optional(),
  admin_comment: z.string().max(1000).optional(),
});

export const submissionFiltersSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  status: submissionStatusSchema.optional(),
  channel: channelSchema.optional(),
  type: submissionTypeSchema.optional(),
  priority: prioritySchema.optional(),
  has_ai_flags: z.coerce.boolean().optional(),
  search: z.string().max(100).optional(),
  sort_by: z.enum(['created_at', 'updated_at', 'priority']).default('created_at'),
  sort_order: z.enum(['asc', 'desc']).default('desc'),
});

// ============================================
// Ban schemas
// ============================================

export const createBanSchema = z.object({
  telegram_user_id: z.string().min(1),
  reason: z.string().max(500).optional(),
  duration_days: z.number().int().min(1).max(365).optional(), // null = permanent
});

export const updateBanSchema = z.object({
  reason: z.string().max(500).optional(),
  expires_at: z.string().datetime().nullable().optional(),
  active: z.boolean().optional(),
});

export const banFiltersSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  active: z.coerce.boolean().optional(),
  search: z.string().max(100).optional(),
});

// ============================================
// Appeal schemas
// ============================================

export const createAppealSchema = z.object({
  message: z.string().min(10, 'Сообщение должно содержать минимум 10 символов').max(1000),
});

export const updateAppealSchema = z.object({
  status: z.enum(['approved', 'rejected']),
  admin_response: z.string().max(1000).optional(),
  new_ban_expires_at: z.string().datetime().optional(), // for reducing ban
});

export const appealFiltersSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  status: appealStatusSchema.optional(),
});

// ============================================
// Admin schemas
// ============================================

export const createAdminSchema = z.object({
  telegram_username: z.string().min(1).max(32),
  role: z.enum(['admin', 'maintainer']), // can't create owner
  notify_channels: z.array(channelSchema).default([]),
});

export const updateAdminSchema = z.object({
  role: z.enum(['admin', 'maintainer']).optional(),
  notify_channels: z.array(channelSchema).optional(),
});

// ============================================
// Settings schemas
// ============================================

export const updateSettingsSchema = z.object({
  channel: channelSchema.nullable().optional(), // null = global
  enabled: z.boolean().optional(),
  disabled_reason: z.string().max(500).optional(),
  greeting_message: z.string().max(1000).optional(),
  daily_submission_limit: z.number().int().min(1).max(100).optional(),
});

// ============================================
// Auth schemas
// ============================================

export const verifyMagicLinkSchema = z.object({
  token: z.string().min(32).max(64),
});

// ============================================
// GitHub issue schemas
// ============================================

export const createGitHubIssueSchema = z.object({
  title: z.string().min(1).max(256),
  labels: z.array(z.string()).default([]),
  body: z.string().max(65536).optional(),
});

// ============================================
// Activity log schemas
// ============================================

export const activityFiltersSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  action: z.string().optional(),
  actor_type: z.enum(['user', 'admin', 'system', 'ai']).optional(),
  target_type: z.string().optional(),
  from_date: z.string().datetime().optional(),
  to_date: z.string().datetime().optional(),
});

// ============================================
// Stats schemas
// ============================================

export const statsQuerySchema = z.object({
  period: z.enum(['7d', '30d', '90d']).default('7d'),
});

export const topSubmittersQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(50).default(10),
});

// ============================================
// Knowledge Base schemas
// ============================================

export const createKBArticleSchema = z.object({
  question: z.string().min(5, 'Вопрос должен содержать минимум 5 символов').max(500),
  answer: z.string().min(10, 'Ответ должен содержать минимум 10 символов').max(2000),
  keywords: z.array(z.string().max(50)).default([]),
  category: z.string().max(50).optional().nullable(),
  priority: z.number().int().min(0).max(100).default(0),
  enabled: z.boolean().default(true),
});

export const updateKBArticleSchema = createKBArticleSchema.partial();

export const kbFiltersSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  category: z.string().max(50).optional(),
  enabled: z.coerce.boolean().optional(),
  search: z.string().max(100).optional(),
});

// ============================================
// AI Configuration schemas
// ============================================

export const updateAIConfigSchema = z.object({
  system_prompt: z.string().min(50, 'Промпт должен содержать минимум 50 символов').max(5000).optional(),
  off_topic_response: z.string().max(500).optional().nullable(),
  escalation_threshold: z.number().int().min(1).max(10).optional(),
  max_history_messages: z.number().int().min(3).max(20).optional(),
  enabled: z.boolean().optional(),
});

// ============================================
// Type exports
// ============================================

export type CreateSubmission = z.infer<typeof createSubmissionSchema>;
export type UpdateSubmission = z.infer<typeof updateSubmissionSchema>;
export type SubmissionFilters = z.infer<typeof submissionFiltersSchema>;

export type CreateBan = z.infer<typeof createBanSchema>;
export type UpdateBan = z.infer<typeof updateBanSchema>;
export type BanFilters = z.infer<typeof banFiltersSchema>;

export type CreateAppeal = z.infer<typeof createAppealSchema>;
export type UpdateAppeal = z.infer<typeof updateAppealSchema>;
export type AppealFilters = z.infer<typeof appealFiltersSchema>;

export type CreateAdmin = z.infer<typeof createAdminSchema>;
export type UpdateAdmin = z.infer<typeof updateAdminSchema>;

export type UpdateSettings = z.infer<typeof updateSettingsSchema>;

export type VerifyMagicLink = z.infer<typeof verifyMagicLinkSchema>;

export type CreateGitHubIssue = z.infer<typeof createGitHubIssueSchema>;

export type ActivityFilters = z.infer<typeof activityFiltersSchema>;

export type StatsQuery = z.infer<typeof statsQuerySchema>;
export type TopSubmittersQuery = z.infer<typeof topSubmittersQuerySchema>;

export type CreateKBArticle = z.infer<typeof createKBArticleSchema>;
export type UpdateKBArticle = z.infer<typeof updateKBArticleSchema>;
export type KBFilters = z.infer<typeof kbFiltersSchema>;

export type UpdateAIConfig = z.infer<typeof updateAIConfigSchema>;
